import { and, eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useLocalSearchParams } from 'expo-router'
import { useCallback } from 'react'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { uploadLocationThumbnail } from '@/lib/locations'
import { performMutations, useMutation } from '@/lib/mutations'
import { newRecordId, useStore } from '@/lib/pocketbase'
import type { LocationMutatableData } from '@/types/locations'

export function useLocations(orgSlug?: string) {
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const slug = orgSlug ?? params.orgSlug

    const [locationsCollection, orgsCollection] = useStore('locations', 'orgs')

    const { isLoading, data = [] } = useLiveQuery(
        q =>
            slug
                ? q
                      .from({ org: orgsCollection })
                      .where(({ org }) => eq(org.slug, slug))
                      .join({ location: locationsCollection }, ({ org, location }) =>
                          eq(location.org, org.id)
                      )
                      .orderBy(({ location }) => location?.name ?? '', 'asc')
                      .select(({ location }) => ({ location }))
                : undefined,
        [slug]
    )

    const locations = data.map(d => d.location).filter((l): l is NonNullable<typeof l> => !!l)

    return {
        locations,
        isPending: isLoading,
    }
}

export function useLocation(locationId: string) {
    const [locationsCollection] = useStore('locations')

    const { isLoading, data: locations = [] } = useLiveQuery(q =>
        q
            .from({ locations: locationsCollection })
            .where(({ locations }) => eq(locations.id, locationId))
    )

    return {
        location: locations[0] ?? null,
        isPending: isLoading,
    }
}

export function useCreateLocation() {
    const { org: currentOrg } = useCurrentOrg()
    const [locationsCollection] = useStore('locations')

    const mutation = useMutation({
        mutationFn: async (data: LocationMutatableData & { job: string }) => {
            if (!currentOrg) throw new Error('No current organization found')

            const id = newRecordId()
            const result = await performMutations(function* () {
                const tx = locationsCollection.insert({
                    ...data,
                    id,
                    description: data.description || '',
                    inactive: data.inactive || false,
                    org: currentOrg.id,
                })

                yield tx
                return tx
            })

            uploadLocationThumbnail({ ...data, id })

            return result
        },
    })

    return {
        createLocation: mutation.mutate,
        isCreating: mutation.isPending,
        resetCreateLocation: mutation.reset,
        createLocationError: mutation.error,
        mutation,
    }
}

export function useUpdateLocation(options?: { onSuccess?: () => void }) {
    const [locationsCollection] = useStore('locations')

    const mutation = useMutation({
        mutationFn: async (data: LocationMutatableData & { id: string }) => {
            await performMutations(function* () {
                yield locationsCollection.update(data.id, draft => {
                    Object.assign(draft, data)
                })
            })

            uploadLocationThumbnail(data)
        },
        onSuccess: options?.onSuccess,
    })

    return {
        updateLocation: mutation.mutate,
        isUpdating: mutation.isPending,
        mutation,
    }
}

export function useDeleteLocation() {
    const [locationsCollection] = useStore('locations')

    const mutation = useMutation({
        mutationFn: async (locationId: string) => {
            await performMutations(function* () {
                yield locationsCollection.delete(locationId)
            })
        },
    })

    return {
        deleteLocation: mutation.mutate,
        isLoading: mutation.isPending,
        mutation,
    }
}

export function useLocationPermissions() {
    const canCreateLocation = useCallback(() => true, [])
    const canEditLocation = useCallback((_location: { org: string }) => true, [])
    const canDeleteLocation = useCallback((_location: { org: string }) => true, [])

    return {
        canCreateLocation,
        canEditLocation,
        canDeleteLocation,
    }
}

export function useLocationsForAssociation() {
    const { locations, isPending } = useLocations()
    return { locations, isPending }
}

export function useJobLocations(jobId: string, options?: { includeInactive?: boolean }) {
    const [locationsCollection] = useStore('locations')

    const { isLoading, data: locations = [] } = useLiveQuery(q =>
        q.from({ locations: locationsCollection }).where(({ locations }) => {
            if (options?.includeInactive) {
                return eq(locations.job, jobId)
            }
            return and(eq(locations.job, jobId), eq(locations.inactive, false))
        })
    )

    return {
        locations,
        isPending: isLoading,
    }
}
